/**
 * TypeScript client for the lao-pdf-api.
 *
 * Use this from your React / Next.js front-end to:
 *   - generate amortization PDFs (download or open)
 *   - preview totals before generating
 *
 * The client calls YOUR Express server (which proxies to the PDF API)
 * — it does NOT call the PDF API directly. That keeps the PDF API
 * private behind your existing auth.
 *
 * Example:
 *   import { generateAmortizationPdf } from "@/lib/pdfClient";
 *
 *   await generateAmortizationPdf({
 *     loan_number: "LN-2026-0042",
 *     principal: 5_000_000,
 *     annual_rate: 0.135,
 *     term_months: 60,
 *     method: "equal_principal",
 *     start_date: "2026-05-26",
 *     institution: { name: "ທະນາຄານ ສຕ ລາວ ຈຳກັດ" },
 *     borrower:    { name: "ນາງ ຈັນທະລາ" },
 *   });
 */

// ---- Types matching the FastAPI schema ----

export type AmortMethod = "equal_payment" | "equal_principal" | "balloon";
export type Currency    = "LAK" | "USD" | "THB" | "CNY";

export interface Institution {
  name:     string;          // Required Lao name
  name_en?: string | null;
  address?: string | null;
  phone?:   string | null;
  email?:   string | null;
  logo?:    string | null;
}

export interface Borrower {
  name:       string;
  id_number?: string | null;
  phone?:     string | null;
  email?:     string | null;
  address?:   string | null;
}

export interface Signatory {
  name:   string;
  title?: string | null;
  label?: string | null;
}

export interface FeeItem {
  label:  string;
  amount: number;
}

export interface AmortizationRequest {
  loan_number: string;
  principal:   number;
  annual_rate: number;          // decimal: 0.135 = 13.5%
  term_months: number;
  method?:     AmortMethod;     // default "equal_principal"
  start_date:  string;          // ISO date "YYYY-MM-DD"
  currency?:   Currency;        // default "LAK"
  balloon_interest_only_periods?: number | null;

  institution: Institution;
  borrower?:   Borrower | null;
  signatories?: Signatory[];
  notes?:      string | null;

  rate_basis?:        string;
  payment_frequency?: string;
  generated_date?:    string | null;

  fees?:       FeeItem[];
  total_fees?: number | null;
  apr?:        number | null;
  payment_method?: string | null;  // UI calculationType: 'balloon' | 'amortization'
}

export interface AmortizationSummary {
  loan_number:      string;
  principal:        number;
  total_interest:   number;
  total_payment:    number;
  payment_first:    number;
  payment_last:     number;
  method:           string;
  method_label_lao: string;
  pages:            number;
  pdf_size_bytes:   number;
  generation_ms:    number;
}

export interface ApiError {
  error:   string;
  message: string;
  detail?: Record<string, unknown>;
}


// ---- Configuration ----

interface ClientConfig {
  /** Base URL for YOUR Express server. Defaults to "" (same-origin). */
  baseUrl?: string;
  /** Optional auth header your Express server expects (cookie-based auth doesn't need this) */
  authHeader?: { name: string; value: string };
  /** Per-request timeout in ms. Default 60000. */
  timeoutMs?: number;
}

const defaultConfig: Required<Pick<ClientConfig, "baseUrl" | "timeoutMs">> = {
  baseUrl:   "",
  timeoutMs: 60_000,
};


// ---- Comparison PDF types ----

export interface ComparisonScenario {
  loan_term:                number;
  interest_rate:            number;
  calculation_type:         string;
  principal_payment_period: number;
  monthly_payment:          number;
  total_interest:           number;
  total_payment:            number;
  is_current:               boolean;
}

export interface ComparisonRequest {
  principal:       number;
  currency?:       Currency;
  scenarios:       ComparisonScenario[];
  institution:     Institution;
  notes?:          string | null;
  generated_date?: string | null;
}


// ---- Public functions ----

/**
 * Generate a comparison PDF and trigger a browser download.
 */
export async function generateComparisonPdf(
  payload: ComparisonRequest,
  config: ClientConfig = {},
): Promise<void> {
  const cfg = { ...defaultConfig, ...config };
  const url = `${cfg.baseUrl}/api/loan/comparison-pdf`;
  const response = await fetchWithTimeout(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(cfg.authHeader ? { [cfg.authHeader.name]: cfg.authHeader.value } : {}),
    },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  }, cfg.timeoutMs);
  if (!response.ok) {
    throw await readApiError(response);
  }
  const blob = await readValidatedPdfBlob(response);
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  const today = new Date().toISOString().split("T")[0];
  a.download = `ປຽບທຽບແຜນການຊຳລະ_${today}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000);
}


/**
 * Generate an amortization PDF and trigger a browser download.
 * Returns the summary headers from the response.
 */
export async function generateAmortizationPdf(
  payload: AmortizationRequest,
  config: ClientConfig = {},
): Promise<AmortizationSummary> {
  const { response, summary } = await postPdfRequest(payload, config);

  // Trigger download
  const blob = await readValidatedPdfBlob(response);
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  // Use Lao filename with today's date for user-friendly download
  const today = new Date().toISOString().split("T")[0];
  a.download = `ຕາຕະລາງຜ່ອນຊຳລະ_${today}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Allow the browser to start the download before revoking
  setTimeout(() => URL.revokeObjectURL(url), 1_000);

  return summary;
}


/**
 * Generate the PDF and return a Blob (so the caller can preview it in
 * an <iframe>, share via the Web Share API, or upload it elsewhere).
 */
export async function getAmortizationPdfBlob(
  payload: AmortizationRequest,
  config: ClientConfig = {},
): Promise<{ blob: Blob; summary: AmortizationSummary }> {
  const { response, summary } = await postPdfRequest(payload, config);
  const blob = await readValidatedPdfBlob(response);
  return { blob, summary };
}


/**
 * Get just the summary (totals, page count) without producing a PDF
 * file the user has to download. Useful for "Preview" buttons that
 * show the schedule on screen before the user commits to downloading.
 */
export async function getAmortizationSummary(
  payload: AmortizationRequest,
  config: ClientConfig = {},
): Promise<AmortizationSummary> {
  const cfg = { ...defaultConfig, ...config };
  const url = `${cfg.baseUrl}/api/loan/amortization-summary`;

  const response = await fetchWithTimeout(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(cfg.authHeader ? { [cfg.authHeader.name]: cfg.authHeader.value } : {}),
    },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  }, cfg.timeoutMs);

  if (!response.ok) {
    throw await readApiError(response);
  }
  return await response.json() as AmortizationSummary;
}


// ---- Internals ----

async function postPdfRequest(
  payload: AmortizationRequest,
  config: ClientConfig,
): Promise<{ response: Response; summary: AmortizationSummary }> {
  const cfg = { ...defaultConfig, ...config };
  const url = `${cfg.baseUrl}/api/loan/amortization-pdf`;

  const response = await fetchWithTimeout(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(cfg.authHeader ? { [cfg.authHeader.name]: cfg.authHeader.value } : {}),
    },
    body: JSON.stringify(payload),
    credentials: "same-origin",
  }, cfg.timeoutMs);

  if (!response.ok) {
    throw await readApiError(response);
  }

  // Decode the summary header (URL-encoded JSON)
  // The header is optional — some proxy setups strip custom headers.
  // If missing or malformed, we build a minimal fallback so the PDF
  // download still works without throwing.
  const rawHeader = response.headers.get("X-Amortization-Summary") ?? "";
  let summary: AmortizationSummary;
  try {
    summary = JSON.parse(decodeURIComponent(rawHeader));
  } catch {
    // Header missing or malformed — use a minimal fallback
    summary = {
      loan_number:      payload.loan_number,
      principal:        payload.principal,
      total_interest:   0,
      total_payment:    0,
      payment_first:    0,
      payment_last:     0,
      method:           payload.method ?? "equal_principal",
      method_label_lao: "",
      pages:            0,
      pdf_size_bytes:   0,
      generation_ms:    0,
    };
  }
  return { response, summary };
}


/**
 * Stops a 200-response HTML/JSON error page from being silently downloaded
 * or passed to the Web Share API as if it were a PDF. We verify bytes rather
 * than relying only on content-type because reverse proxies may alter headers.
 */
export async function readValidatedPdfBlob(response: Response): Promise<Blob> {
  const blob = await response.blob();
  if (blob.size < 5) {
    throw { error: "INVALID_PDF", message: "PDF ທີ່ສ້າງມາບໍ່ສົມບູນ" } satisfies ApiError;
  }

  const signature = await blob.slice(0, 5).text();
  if (signature !== "%PDF-") {
    throw { error: "INVALID_PDF", message: "ລະບົບບໍ່ສາມາດສ້າງໄຟລ໌ PDF ທີ່ຖືກຕ້ອງ" } satisfies ApiError;
  }
  return blob;
}

async function readApiError(response: Response): Promise<ApiError> {
  try {
    const body = await response.json();
    if (body && typeof body.error === "string") {
      return body as ApiError;
    }
  } catch { /* not JSON */ }
  return {
    error:   `HTTP_${response.status}`,
    message: response.statusText || "Request failed",
  };
}


async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      const e: ApiError = {
        error:   "TIMEOUT",
        message: `Request timed out after ${timeoutMs}ms`,
      };
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
